import React, { ReactNode, useCallback, useMemo, useRef } from 'react'

import { Nullable } from '@types'

import ConditionalLinkWrapper from '@components/ConditionalLinkWrapper'
import CustomColorTooltip from '@components/CustomColorTooltip'
import { AttendeeItem } from '@modules/profile/types'
import { Box } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useElementResize } from '@shared/hooks/useElementResize'
import { colors } from '@theme/variables'

import CardImage from './CardImage'
import CardsListPopover from './CardsListPopover'
import {
  CARD_IMAGE_DIMENSION,
  CARD_IMAGE_OVERLAP,
  CardTooltipTitle,
  CardTooltipWrapper,
  CardsListWrapper,
  CardsListWrapperInner,
  PopoverItemCard,
} from './index.styles'

interface CardInfoProps {
  cardInfoTitle?: string
  cardInfoDescription?: string
}
interface CardsListItemProps {
  cardLinkUrl?: string
  imageSrc?: string
  imageAltInfo: string
  isDisabled: boolean
  isImported?: boolean
  cardInfo?: CardInfoProps
  raw: AttendeeItem
}
interface CardsListProps {
  itemWidth?: number
  onLoadMore?: () => void
  cardsItems: CardsListItemProps[]
  totalCardsAmount?: number
  containerWidth?: number
  renderActionButton?: (i: AttendeeItem, isTooltipView?: boolean) => ReactNode
}

const CardsList = (props: CardsListProps) => {
  const {
    onLoadMore,
    cardsItems,
    totalCardsAmount,
    itemWidth = CARD_IMAGE_DIMENSION,
    containerWidth: containerWidthFromParent,
    renderActionButton,
  } = props

  const cardsListContainerRef = useRef<Nullable<HTMLDivElement>>(null)
  const { elementWidth } = useElementResize(cardsListContainerRef)
  const totalCardsCount = useMemo(() => totalCardsAmount || cardsItems.length, [totalCardsAmount, cardsItems])

  const visibleCardsCount = useMemo(() => {
    const containerWidth = containerWidthFromParent || elementWidth || 0
    const availableCountToDisplay = Math.floor((containerWidth - CARD_IMAGE_OVERLAP) / (itemWidth - CARD_IMAGE_OVERLAP))

    const count =
      availableCountToDisplay < totalCardsCount
        ? availableCountToDisplay - 1
        : Math.min(availableCountToDisplay, cardsItems.length)

    return Math.max(1, count)
  }, [containerWidthFromParent, elementWidth, cardsItems, itemWidth, totalCardsCount])

  const renderCardContent = (data: {
    contentData: CardInfoProps
    linkUrl: string
    attendee: AttendeeItem
    isImported?: boolean
    styleProps?: { verticalPadding: number; horizontalPadding: number }
  }) => {
    const { attendee, isImported, styleProps, contentData, linkUrl } = data
    if (attendee.isHidden && !isImported) {
      return null
    }

    const { cardInfoTitle, cardInfoDescription } = contentData

    return (
      <CardTooltipWrapper verticalPadding={styleProps?.verticalPadding} horizontalPadding={styleProps?.horizontalPadding}>
        <Box>
          {!isImported ? (
            <ConditionalLinkWrapper
              linkUrl={linkUrl}
              childElement={<CardTooltipTitle variant="h3">{cardInfoTitle}</CardTooltipTitle>}
            />
          ) : (
            <Typography variant="h3">{cardInfoTitle}</Typography>
          )}
          {cardInfoDescription && <Typography variant="body1">{cardInfoDescription}</Typography>}
        </Box>
        {/* it is a quick solution, the component logic requires full refactoring to use it without such tricks */}
        {!attendee.isHidden && renderActionButton?.(attendee, !styleProps)}
      </CardTooltipWrapper>
    )
  }

  const renderCardImage = (data: {
    imageSrc?: string
    imageAltInfo?: string
    cardLinkUrl?: string
    isDisabled?: boolean
    overlapSize?: number
    cardImageWidth?: number
    isImported?: boolean
  }) => {
    const {
      imageSrc,
      imageAltInfo,
      cardLinkUrl,
      isDisabled,
      cardImageWidth = CARD_IMAGE_DIMENSION,
      overlapSize = 0,
      isImported,
    } = data
    const cardImage = (
      <CardImage
        imageSrc={imageSrc}
        imageAltInfo={imageAltInfo}
        overlapSize={overlapSize}
        itemWidth={cardImageWidth}
        isDisabled={isDisabled && !isImported}
        isClickable={!isImported}
      />
    )

    return <ConditionalLinkWrapper linkUrl={isDisabled ? '' : cardLinkUrl} childElement={cardImage} />
  }

  const renderPopoverCardsList = useCallback(() => {
    return cardsItems.slice(visibleCardsCount).map(popoverCardItem => {
      const { cardLinkUrl = '', cardInfo = {}, imageSrc, imageAltInfo, isDisabled, isImported } = popoverCardItem

      if (isDisabled && !isImported) {
        return null
      }

      return (
        <PopoverItemCard key={imageAltInfo + cardLinkUrl}>
          {renderCardImage({ imageSrc, imageAltInfo, cardLinkUrl, isDisabled, isImported })}
          {renderCardContent({ contentData: cardInfo, linkUrl: cardLinkUrl, attendee: popoverCardItem.raw, isImported })}
        </PopoverItemCard>
      )
    })
    // eslint-disable-next-line
  }, [visibleCardsCount, cardsItems.length])

  return (
    <CardsListWrapper data-testid="card-list-wrapper">
      <CardsListWrapperInner ref={cardsListContainerRef}>
        {cardsItems.slice(0, visibleCardsCount).map((card: CardsListItemProps, idx: number, arr) => {
          const { cardLinkUrl = '', imageSrc = '', imageAltInfo, cardInfo = {}, isDisabled, isImported } = card

          return (
            <CustomColorTooltip
              title={renderCardContent({
                isImported,
                contentData: cardInfo,
                linkUrl: cardLinkUrl,
                attendee: card.raw,
                styleProps: { verticalPadding: 0, horizontalPadding: 15 },
              })}
              key={imageAltInfo + cardLinkUrl}
              data-testid="card-item-wrapper"
              bgColor={colors.white}
            >
              {/* div is necessary here, don't remove, otherwise tooltip will not be shown */}
              <div>
                {renderCardImage({
                  imageSrc,
                  imageAltInfo,
                  cardLinkUrl,
                  isDisabled,
                  overlapSize: idx === arr.length - 1 && cardsItems.length === visibleCardsCount ? 0 : -CARD_IMAGE_OVERLAP,
                  cardImageWidth: itemWidth,
                  isImported,
                })}
              </div>
            </CustomColorTooltip>
          )
        })}
        {visibleCardsCount < totalCardsCount && (
          <CardsListPopover
            itemWidth={itemWidth}
            buttonText={`+${totalCardsCount - visibleCardsCount}`}
            popoverId="attendees-popover"
            items={renderPopoverCardsList()}
            onLoadMore={onLoadMore}
          />
        )}
      </CardsListWrapperInner>
    </CardsListWrapper>
  )
}

export default CardsList
